'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { cn } from '@/src/shared/lib/utils';
import { Check } from 'lucide-react';

interface Locale {
  code: string;
  label: string;
}

interface LanguageSelectorProps {
  currentLocale: string;
  availableLocales: Locale[];
  onLocaleChange: (locale: string) => void;
  label: string;
}

export function LanguageSelector({
  currentLocale,
  availableLocales,
  onLocaleChange,
  label,
}: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 160; // w-40 = 10rem = 160px
      const viewportWidth = window.innerWidth;

      // Рассчитываем позицию: по умолчанию привязка к левому краю кнопки
      const wouldOverflowRight = buttonRect.left + dropdownWidth > viewportWidth;
      const wouldOverflowLeft = buttonRect.right - dropdownWidth < 0;

      // Если выпадет за правый край - привязываем к правому краю кнопки
      if (wouldOverflowRight && !wouldOverflowLeft) {
        setDropdownPosition('right');
      } else {
        setDropdownPosition('left');
      }
    }
  }, [isOpen]);

  const handleLocaleChange = (locale: string) => {
    onLocaleChange(locale);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-desktop-body-sm font-medium text-text-primary transition-colors hover:bg-background-2"
        aria-label={label}
      >
        <Image
          src="/images/icons/language.svg"
          alt="Language"
          width={20}
          height={20}
          className="h-5 w-5"
        />
        <span className="uppercase">{currentLocale}</span>
        <Image
          src="/images/icons/arrow-down.svg"
          alt="Language"
          width={20}
          height={20}
          className="h-5 w-5"
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            className={cn(
              "absolute top-full z-50 mt-2 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg",
              dropdownPosition === 'left' ? 'left-0' : 'right-0'
            )}
          >
            {availableLocales.map((loc) => (
              <button
                key={loc.code}
                onClick={() => handleLocaleChange(loc.code)}
                className={cn(
                  'flex w-full items-center justify-between px-4 py-3 text-left text-desktop-body-sm transition-colors hover:bg-accent-1/5',
                  currentLocale === loc.code
                    ? 'bg-accent-1/10 text-accent-1'
                    : 'text-text-primary'
                )}
              >
                <span>{loc.label}</span>
                {currentLocale === loc.code && (
                  <Check className="h-4 w-4 text-accent-1" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
